import React from "react";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useParams } from "react-router-dom";

import { publicPassApi } from "../../api";
import type { PublicPass } from "../../api";
import "../../components/ticketTemplate/ticketTemplate.css";
import "../../components/ticketTemplate/zoniqTicketTemplate.css";

const STATE_COPY: Record<PublicPass["state"], { title: string; text: string }> = {
  valid: { title: "You're in", text: "Show this QR at the gate." },
  checked_in: { title: "Already used", text: "This pass has been scanned and can't be used again." },
  payment_due: { title: "Pay at the gate", text: "Show this QR at the gate and pay to enter." },
  void: { title: "Pass not valid", text: "This pass was cancelled. If you paid, your refund is on its way." },
};

function formatInr(amount: number): string {
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);
}

/** A guest's entry pass: the signed QR the gate scans. The URL token is the only credential. */
const PublicPassPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const passQuery = useQuery({
    queryKey: ["public-pass", token],
    queryFn: () => publicPassApi.pass(token!),
    enabled: Boolean(token),
    retry: false,
  });

  if (passQuery.isPending) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body">
            <p className="zticket-hint">Loading your pass…</p>
          </div>
        </div>
      </div>
    );
  }

  if (passQuery.isError || !passQuery.data) {
    return (
      <div className="zticket-page">
        <div className="zticket-card">
          <div className="zticket-body zticket-status">
            <h2 className="zticket-status__title">Pass not found</h2>
            <p className="zticket-status__text">This link isn&apos;t valid. Open the pass link from your message or email again.</p>
          </div>
        </div>
      </div>
    );
  }

  const pass: PublicPass = passQuery.data;
  const copy = STATE_COPY[pass.state];
  const when = [pass.event_date ? new Date(pass.event_date).toLocaleDateString("en-IN", { dateStyle: "long" }) : null, pass.event_time]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="zticket-page">
      <div className="zticket-card">
        <div className="zticket-body zticket-status">
          <p className="zticket-hint">{pass.event_name}</p>
          <h2 className="zticket-status__title">{copy.title}</h2>
          <p className="zticket-status__text">{copy.text}</p>

          {pass.qr_token ? (
            <div style={{ background: "#fff", padding: 16, borderRadius: 12, width: "fit-content", margin: "12px auto" }}>
              <QRCodeSVG value={pass.qr_token} size={220} level="M" aria-label="Entry pass QR code" />
            </div>
          ) : null}

          {pass.state === "payment_due" && pass.amount_due > 0 ? (
            <p className="zticket-status__text" style={{ fontWeight: 600 }}>
              Amount to pay at the gate: {formatInr(pass.amount_due)}
            </p>
          ) : null}

          <div className="zticket-hint" style={{ lineHeight: 1.6 }}>
            <div>
              <strong>{pass.holder_name || "Guest"}</strong>
              {pass.tier_label ? ` · ${pass.tier_label}` : ""}
              {pass.admits > 1 ? ` · admits ${pass.admits}` : ""}
            </div>
            {when ? <div>{when}</div> : null}
            {pass.venue ? <div>{[pass.venue, pass.city].filter(Boolean).join(", ")}</div> : null}
            <div style={{ opacity: 0.7 }}>{pass.ticket_number}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicPassPage;
